import { useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { UserForgotPasswordForm } from "~/lib/schemas/user-auth";
import { UserForgotPasswordSchema } from "~/lib/schemas/user-auth";
import { ChevronLeft } from "lucide-react";
import { isClerkAPIResponseError } from "~/lib/clerk";
import { createNoIndexMeta } from "~/lib/shared/meta";

import { Card } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Button } from "~/components/ui/button";
import {
  MultiStepForm,
  useMultiStepForm,
} from "~/components/ui/multi-step-form";
import { OtpInput } from "~/components/ui/otp-input";
import { FormField } from "~/components/ui/form-field";

import { useClerk } from "@clerk/tanstack-react-start";

// --- Step Components ---

const EmailStep = ({
  onNext,
  isProcessing,
  error,
}: {
  onNext: () => Promise<void>;
  isProcessing: boolean;
  error?: string;
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<UserForgotPasswordForm>();
  const { next } = useMultiStepForm();

  const handleNext = async () => {
    try {
      await onNext();
      next();
    } catch {
      // Error is handled by the parent component, don't proceed to next step
    }
  };

  return (
    <div className="w-full flex-shrink-0 space-y-8 px-1">
      <div className="space-y-2">
        <Link
          to="/signin"
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          <span className="inline-flex items-center">
            <ChevronLeft /> Log In
          </span>
        </Link>
        <h2 className="text-xl font-semibold">Forgot password</h2>
      </div>
      <div className="space-y-2">
        <FormField
          fullWidth
          label="Enter email or phone number"
          error={errors.email?.message || error}
        >
          <Input
            {...register("email")}
            type="email"
            placeholder="Enter email or phone number"
            className={errors.email ? "border-red-500" : ""}
          />
        </FormField>
      </div>
      <Button
        type="button"
        loading={isProcessing}
        disabled={isProcessing}
        className="mb-2 w-full"
        onClick={handleNext}
      >
        Reset Password
      </Button>
      <div id="clerk-captcha" className="w-full" />
    </div>
  );
};

const OtpStep = ({
  onNext,
  onResend,
  isProcessing,
  canResend,
  resendTimer,
  email,
  error,
  code,
  onOtpChange,
}: {
  onNext: (code: string) => Promise<void>;
  onResend: () => void;
  isProcessing: boolean;
  canResend: boolean;
  resendTimer: number;
  email: string;
  error: string;
  code: string[];
  onOtpChange: (code: string[]) => void;
}) => {
  const { next } = useMultiStepForm();

  const handleNext = async (code: string) => {
    try {
      await onNext(code);
      next();
    } catch {
      // Error is handled by the parent component, don't proceed to next step
    }
  };

  return (
    <div className="w-full flex-shrink-0 px-1">
      <div className="space-y-2">
        <h1 className="font-heading text-3xl">Check your email</h1>
        <h3 className="text-text-secondary font-heading">
          {`We sent a code to ${email}. Enter 6 digit code that mentioned in the email`}
        </h3>
      </div>
      <div className="my-8 flex items-end gap-4 transition-all duration-300 ease-in-out">
        <OtpInput
          value={code}
          error={error}
          onChange={onOtpChange}
          onComplete={handleNext}
        />
      </div>
      <Button
        type="button"
        className="mb-2 w-full"
        onClick={onResend}
        disabled={!canResend}
        loading={isProcessing}
      >
        {canResend ? "Resend code" : `Resend code (${resendTimer}s)`}
      </Button>
    </div>
  );
};

const NewPasswordStep = ({
  onNext,
  isProcessing,
}: {
  onNext: () => Promise<void>;
  isProcessing: boolean;
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<UserForgotPasswordForm>();
  const { next } = useMultiStepForm();

  const handleNext = async () => {
    try {
      await onNext();
      next();
    } catch {
      // Error is handled by the parent component, don't proceed to next step
    }
  };

  return (
    <div className="w-full flex-shrink-0 space-y-8 px-1">
      <div className="space-x-2">
        <h1 className="font-heading text-3xl">Set a New Password</h1>
        <h3 className="text-text-secondary font-heading">
          Create a new password. Ensure it differs from previous ones for
          security
        </h3>
      </div>
      <div>
        <div className="space-y-2">
          <FormField
            fullWidth
            label="Create Password"
            error={errors.password?.message}
          >
            <Input
              {...register("password")}
              autoComplete="off"
              type="password"
              placeholder="Create Password"
              className={errors.password ? "border-red-500" : ""}
            />
          </FormField>
        </div>
        <div className="space-y-2">
          <FormField
            fullWidth
            label="Confirm Password"
            error={errors.password2?.message}
          >
            <Input
              {...register("password2")}
              autoComplete="off"
              type="password"
              placeholder="Confirm Password"
              className={errors.password2 ? "border-red-500" : ""}
            />
          </FormField>
        </div>
      </div>
      <Button
        type="button"
        className="mb-2 w-full"
        loading={isProcessing}
        disabled={isProcessing}
        onClick={handleNext}
      >
        Update Password
      </Button>
    </div>
  );
};

export const Route = createFileRoute("/_auth/forgot-password/")({
  head: () => {
    const metaConfig = createNoIndexMeta(
      "Forgot Password – Dream Coach Match",
      "Reset your password to regain access to your Dream Coach Match account. Enter your email to receive password reset instructions.",
    );

    return {
      ...metaConfig,
      links: [
        {
          rel: "canonical",
          href: "https://dreamcoachmatch.com/forgot-password",
        },
      ],
    };
  },
  component: ForgotPasswordPage,
});

const ForgotPasswordComponent = () => {
  const form = useForm<UserForgotPasswordForm>({
    resolver: zodResolver(UserForgotPasswordSchema),
    defaultValues: {
      email: "",
      password: "",
      password2: "",
    },
  });
  const { handleSubmit, watch, getValues } = form;

  const navigate = useNavigate();
  const clerk = useClerk();

  const [isProcessing, setIsProcessing] = useState(false);
  const [state, setState] = useState({
    code: ["", "", "", "", "", ""],
    error: "",
    resendTimer: 60,
    canResend: false,
  });

  const startResendTimer = () => {
    setState((prev) => ({
      ...prev,
      resendTimer: 60,
      canResend: false,
    }));

    const timer = setInterval(() => {
      setState((prev) => {
        const newTimer = prev.resendTimer - 1;
        if (0 >= newTimer) {
          clearInterval(timer);
          return { ...prev, resendTimer: 0, canResend: true };
        }
        return { ...prev, resendTimer: newTimer };
      });
    }, 1000);
  };

  const handleSendOtp = async (): Promise<void> => {
    const isValid = await form.trigger(["email"]);
    if (!isValid) {
      throw new Error("Form validation failed");
    }

    startResendTimer();
    setState((prev) => ({ ...prev, error: "" }));
    setIsProcessing(true);
    const emailAddress = getValues("email");

    try {
      if (!clerk?.client) {
        throw new Error("Clerk client not available");
      }

      await clerk.client.signIn.create({ identifier: emailAddress });
      await clerk.client.signIn.prepareFirstFactor({
        strategy: "reset_password_email_code",
        emailAddressId: "email", // This should be the actual email address ID from the signIn attempt
      });

      setIsProcessing(false);
    } catch (err) {
      setIsProcessing(false);
      if (isClerkAPIResponseError(err)) {
        setState((prev) => ({
          ...prev,
          error: err.errors[0]?.longMessage || "Failed to send reset code",
        }));
      } else {
        setState((prev) => ({
          ...prev,
          error: "An unexpected error occurred.",
        }));
      }
      throw err; // Re-throw to prevent step progression
    }
  };

  const handleOtpComplete = async (code: string): Promise<void> => {
    setIsProcessing(true);

    try {
      if (!clerk?.client) {
        throw new Error("Clerk client not available");
      }

      const signInAttempt = await clerk.client.signIn.attemptFirstFactor({
        strategy: "reset_password_email_code",
        code,
      });

      if ("complete" !== signInAttempt.status) {
        setIsProcessing(false);
        throw new Error("OTP verification incomplete");
      }

      setIsProcessing(false);
    } catch (err) {
      setIsProcessing(false);
      if (isClerkAPIResponseError(err)) {
        setState((prev) => ({
          ...prev,
          error: err.errors[0]?.longMessage || "Failed to verify OTP code",
        }));
      } else {
        setState((prev) => ({
          ...prev,
          error: "An unexpected error occurred.",
        }));
      }
      throw err; // Re-throw to prevent step progression
    }
  };

  const handleVerifyOtp = async (): Promise<void> => {
    const isValid = await form.trigger(["password", "password2"]);
    if (!isValid) {
      throw new Error("Form validation failed");
    }

    setIsProcessing(true);

    try {
      if (!clerk?.client) {
        throw new Error("Clerk client not available");
      }

      const verify = await clerk.client.signIn.attemptFirstFactor({
        strategy: "reset_password_email_code",
        code: state.code.join(""),
        password: getValues("password"),
      });

      if ("complete" === verify.status && verify.createdSessionId) {
        await clerk.setActive({ session: verify.createdSessionId });
        navigate({ to: "/settings" });
      }
    } catch (err) {
      if (isClerkAPIResponseError(err)) {
        setState((prev) => ({
          ...prev,
          error: err.errors[0]?.longMessage || "Failed to reset password",
        }));
      } else {
        setState((prev) => ({
          ...prev,
          error: "An unexpected error occurred.",
        }));
      }
      throw err; // Re-throw to prevent step progression
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOtpChange = (code: string[]) => {
    setState((prev) => ({ ...prev, code: [...code], error: "" }));
  };

  const watchedEmail = watch("email");

  return (
    <FormProvider {...form}>
      <form
        onSubmit={handleSubmit(() => {
          /* Handled by step buttons */
        })}
      >
        <Card className="sm:w-145 w-100 flex flex-col p-8 hover:shadow">
          <MultiStepForm>
            <EmailStep
              onNext={handleSendOtp}
              isProcessing={isProcessing}
              error={state.error}
            />
            <OtpStep
              onNext={handleOtpComplete}
              onResend={handleSendOtp}
              isProcessing={isProcessing}
              canResend={state.canResend}
              resendTimer={state.resendTimer}
              email={watchedEmail}
              error={state.error}
              code={state.code}
              onOtpChange={handleOtpChange}
            />
            <NewPasswordStep
              onNext={handleVerifyOtp}
              isProcessing={isProcessing}
            />
          </MultiStepForm>
          <div className="font-400 mt-4 text-center text-sm">
            Don't have an account?{" "}
            <Link
              to="/signup"
              className="text-text-primary/100 font-600 hover:underline"
            >
              Sign up
            </Link>
          </div>
        </Card>
      </form>
    </FormProvider>
  );
};

function ForgotPasswordPage() {
  return <ForgotPasswordComponent />;
}
